/**
 * Canonical provider-error sanitization. Strips API keys, replaces
 * CONSUMER_SUSPENDED / PERMISSION_DENIED with a stable human-readable
 * message, and caps length to keep log lines bounded.
 *
 * Replaces three near-identical private copies that lived in:
 *   - src/core/embeddings/gemini.ts (safeEmbeddingErrorMessage)
 *   - src/lib/gemini-safe.ts (safeProviderErrorMessage)
 *   - src/lib/tool-logic.ts (sanitizeProviderError)
 *
 * Use this anywhere a provider error message is about to land in a log
 * line, log context bag, or user-visible error.
 */
export function sanitizeProviderErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  const redacted = raw
    .replace(/api_key:[^'",}\\\s]+/gi, 'api_key:[redacted]')
    .replace(/AIza[0-9A-Za-z_-]{20,}/g, '[redacted-api-key]')

  if (/CONSUMER_SUSPENDED|has been suspended/i.test(redacted)) {
    return 'Google Generative Language API key is suspended (CONSUMER_SUSPENDED).'
  }

  if (/PERMISSION_DENIED/i.test(redacted)) {
    return 'Google Generative Language API permission denied.'
  }

  return redacted.slice(0, 240)
}

/**
 * Sanitize a raw string (e.g. parsed from an arbitrary server payload)
 * through the same redaction rules. Use when the input is not an Error
 * but a string that may contain provider credentials.
 */
export function sanitizeProviderString(raw: string): string {
  return sanitizeProviderErrorMessage(raw)
}
